import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@r8er/components/ui/select";
import { classes } from "@r8er/lib/utils";
import type { ComponentPropsWithoutRef, InputHTMLAttributes } from "react";
import { useState } from "react";
import styles from "./Input.module.css";
import type { OnInputChangeCB } from "./onChange";

interface OptionProps extends InputHTMLAttributes<HTMLOptionElement> {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface InputSelectProps
  extends Omit<
    ComponentPropsWithoutRef<typeof SelectTrigger>,
    "children" | "defaultValue" | "onChange" | "type"
  > {
  id: string;
  defaultValue?: InputHTMLAttributes<HTMLSelectElement>["defaultValue"];
  placeholder?: string;
  required?: boolean;

  onChange?: OnInputChangeCB;

  options: OptionProps[];

  theme?: {
    selectContainer?: string;
    selectButton?: string;
    selectedOption?: string;
    selectedOptionButton?: string;
    unselectedOption?: string;
    arrowDown?: string;
    selectOptions?: string;
    selectOptionsContent?: string;
    visibleSelectOptions?: string;
    selectOption?: string;
    selectedOptionContainer?: string;
    activeButton?: string;
    activeContainer?: string;
  };
}

export const InputSelect = ({
  id,
  name,
  defaultValue,
  onChange,
  options,
  theme,
  className,
  disabled,
  placeholder,
  required,
  ...props
}: InputSelectProps) => {
  const [oldValue, setOldValue] = useState(defaultValue);
  return (
    <Select
      name={name}
      defaultValue={defaultValue?.toString()}
      disabled={disabled}
      required={required}
      onValueChange={(newValue) => {
        if (onChange)
          onChange({
            event: undefined,
            newValue,
            oldValue,
          });
        setOldValue(newValue);
      }}
    >
      <SelectTrigger
        id={id}
        {...props}
        aria-required={required || props["aria-required"] || undefined}
        className={classes(
          styles.selectContainer,
          theme?.selectContainer,
          styles.selectButton,
          theme?.selectButton,
          className,
        )}
      >
        <SelectValue placeholder={placeholder ?? "Select..."} />
      </SelectTrigger>
      <SelectContent
        collisionPadding={{
          top: 210,
          bottom: 10,
        }}
        className={classes(
          styles.selectOptions,
          theme?.selectOptions,
          styles.selectOptionsContent,
          theme?.selectOptionsContent,
        )}
      >
        {options
          .filter((o) => o.disabled !== true || o.value === defaultValue)
          .map((option) => (
            <SelectItem
              key={`select_${name}_${option.value}`}
              className={classes(styles.selectOption, theme?.selectOption)}
              value={option.value}
              disabled={option.disabled ?? false}
            >
              {option.label}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};
